import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api, getToken } from "../api/client";
import type { AgentBuild, ModuleOut } from "../api/types";
import { Badge, Empty, ErrorBox, Loading, relativeTime } from "../components/common";
import PhonePreview, { FRAME_WIDTH } from "../design/PhonePreview";
import { moduleName, moduleUsage, sampleRows } from "../design/modules";
import { REQUIRED_ACTION, type Spec } from "../design/spec";
import { qaDesktop, type BuildStage, type LocalBuildBundle } from "../desktop";
import { useI18n, type Translate } from "../i18n";
import { localized } from "../i18n/localized";

/** While anything is pending or building, poll — a stamp takes a few seconds. */
const POLL_MS = 3000;

/** Beside the form: enough to recognise the screen, not enough to crowd it. */
const PICKER_SCALE = 140 / FRAME_WIDTH;

/** What a build reports when the customer dismissed the save dialog. English
 *  on purpose: it lands in `agent_builds.error`, which our support reads as
 *  often as the customer does (DESKTOP §4.3). */
const CANCELLED = "Cancelled before saving";

/** `qa-agent-warehouse-0.5.0.apk` — the name the save dialog opens with.
 *
 * Read off the bundle rather than off the form, so it matches what the CLI and
 * the app's own fallback produce (`pipeline.ts` `suggestedFileName`, §5.1): the
 * batch label when there is one, otherwise the tenant's slug. The form's label
 * is the same string, but only when the customer typed one. */
function apkFileName(bundle: LocalBuildBundle): string {
  const config = (bundle.config ?? {}) as { label?: string | null; tenant?: string };
  const who = (config.label || config.tenant || "agent").replace(/[^A-Za-z0-9._-]+/g, "-");
  return `qa-agent-${who}-${bundle.template.agent_version}.apk`;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** What the customer reads under the form while their computer works.
 *
 * Spelled out rather than interpolated into the key, so the i18n test can see
 * all five and tell us when one is missing from a dictionary. `key` shows only
 * on the very first build, which spends several seconds making an RSA-4096
 * key before anything is downloaded. */
function stageText(stage: BuildStage, t: Translate): string {
  if (stage === "key") return t("apk.stageKey");
  if (stage === "download") return t("apk.stageDownload");
  if (stage === "stamp") return t("apk.stageStamp");
  if (stage === "sign") return t("apk.stageSign");
  return t("apk.stageSave");
}

function isWorking(builds: AgentBuild[] | undefined): boolean {
  return (builds ?? []).some((b) => b.status === "pending" || b.status === "building");
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/**
 * *Location (2 actions, 2 values, 0 blocks)* — what the chosen design asks of
 * each module, counted off the document itself (MODULES §8.7).
 */
function usesLine(
  spec: Spec | undefined,
  modules: ModuleOut[],
  lang: string,
  t: Translate,
): string {
  return moduleUsage(spec)
    .map((use) =>
      t("apk.usesModule", {
        module: moduleName(modules, use.module, lang),
        actions: use.actions.length,
        values: use.rows.length,
        blocks: use.blocks.length,
      }),
    )
    .join(", ");
}

export default function AgentApk() {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  // Read here rather than at import time: the bridge is injected by the
  // desktop app's preload, and a test installs a fake one per case (§7).
  const app = qaDesktop();
  const [label, setLabel] = useState("");
  const [keyId, setKeyId] = useState("");
  const [cap, setCap] = useState("");
  // "" is the built-in one-button screen; `?design=` from the editor's
  // "Generate APK with this design" wins over the tenant's default.
  const [params] = useSearchParams();
  const [designId, setDesignId] = useState<string | null>(null);

  const builds = useQuery({
    queryKey: ["agent-builds"],
    queryFn: api.agentBuilds,
    refetchInterval: (query) => (isWorking(query.state.data) ? POLL_MS : false),
  });
  const keys = useQuery({ queryKey: ["enrollment-keys"], queryFn: api.enrollmentKeys });
  const designs = useQuery({ queryKey: ["screen-designs"], queryFn: api.screenDesigns });
  // What this build will carry: the customer's enabled set, which is also
  // what `modules: null` means to the API (MODULES §8.4).
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });

  // Preselect once the list arrives: the link's design if it named one,
  // otherwise the tenant's default — which is what a rollout would use.
  useEffect(() => {
    if (designId !== null || !designs.data) return;
    const wanted = params.get("design");
    const found = wanted && designs.data.some((d) => d.id === wanted) ? wanted : null;
    setDesignId(found ?? designs.data.find((d) => d.is_default)?.id ?? "");
  }, [designs.data, params, designId]);

  const chosen = designs.data?.find((d) => d.id === designId) ?? null;

  const request = () => ({
    label: label.trim() || null,
    enrollment_key_id: keyId || null,
    max_devices: cap ? Number(cap) : null,
    design_id: designId || null,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["agent-builds"] });
    queryClient.invalidateQueries({ queryKey: ["enrollment-keys"] });
  };

  const generate = useMutation({
    mutationFn: () => api.createAgentBuild(request()),
    onSuccess: () => {
      setLabel("");
      setCap("");
      refresh();
    },
  });

  // --- building on this computer (DESKTOP §7) -------------------------------

  const [stage, setStage] = useState<BuildStage | null>(null);
  const [savedTo, setSavedTo] = useState<string | null>(null);

  useEffect(() => app?.onBuildProgress(setStage), [app]);

  /**
   * Prepare on the server, stamp and sign here, then say what happened.
   *
   * The report is not optional and not best-effort: the row exists from the
   * moment the bundle is handed over, so every path out of here — a thrown
   * stage, a cancelled dialog, a finished file — ends in `reportLocalBuild`
   * (§4.3). Otherwise the builds table keeps a row that never finished.
   */
  const buildHere = useMutation({
    mutationFn: async () => {
      const prepared = await api.createLocalAgentBuild(request());
      const name = apkFileName(prepared.bundle);
      try {
        // The app is handed the row's id and this session's token as well as
        // the bundle: quitting with the save dialog open is the one ending
        // this page never sees, and the app reports it instead (§4.3).
        const outcome = await app!.buildApk(prepared.bundle, name, {
          buildId: prepared.build.id,
          token: getToken() ?? "",
        });
        if ("cancelled" in outcome) {
          await api.reportLocalBuild(prepared.build.id, { error: CANCELLED });
          return null;
        }
        await api.reportLocalBuild(prepared.build.id, {
          sha256: outcome.sha256,
          size: outcome.size,
        });
        return outcome.path;
      } catch (error) {
        await api.reportLocalBuild(prepared.build.id, {
          error: messageOf(error).slice(0, 2000),
        });
        throw error;
      }
    },
    onSuccess: (path) => {
      setSavedTo(path);
      if (path !== null) {
        setLabel("");
        setCap("");
      }
    },
    // Either way the table has a new row and the keys page may have a new key.
    onSettled: () => {
      setStage(null);
      refresh();
    },
  });

  // Storage serves the APK as an attachment, so sending the browser at the
  // URL saves the file instead of navigating away from the console.
  const download = useMutation({
    mutationFn: (id: string) => api.agentBuildDownloadUrl(id),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  // The signing key that belongs to this computer (DESKTOP §6, D4). Only the
  // app can answer, so the query simply does not run in a browser.
  const signingKey = useQuery({
    queryKey: ["signing-key"],
    queryFn: () => app!.signingKey(),
    enabled: app !== undefined,
  });
  const [exportPassword, setExportPassword] = useState("");
  const [importPassword, setImportPassword] = useState("");
  const exportKey = useMutation({ mutationFn: () => app!.exportSigningKey(exportPassword) });
  const importKey = useMutation({
    mutationFn: () => app!.importSigningKey(importPassword),
    onSuccess: () => {
      setImportPassword("");
      queryClient.invalidateQueries({ queryKey: ["signing-key"] });
    },
  });

  const problem = generate.error ?? buildHere.error;
  const failure = problem instanceof ApiError ? problem : null;
  const noTemplate = failure?.code === "no_agent_template";
  const designUnsupported = failure?.code === "design_unsupported";
  // The release predates the module, or the design names one that is off:
  // two refusals a customer can act on, so neither shows as its code (§8.4).
  const moduleUnsupported = failure?.code === "module_unsupported";
  const moduleOff = failure?.code === "design_module_disabled";
  // A key whose plaintext the server cannot produce again: refused before
  // a build row exists, on both the server and the desktop path.
  const keyUnusable = failure?.code === "key_unavailable";

  const catalogue = modules.data ?? [];
  const enabledModules = catalogue.filter((module) => module.enabled);
  const uses = usesLine(chosen?.spec, catalogue, lang, t);

  // The install steps should name the button the phone will actually show.
  const accessibilityLabel =
    chosen?.spec.blocks.find(
      (block) => block.type === "button" && block.action.type === REQUIRED_ACTION,
    ) ?? null;
  const install2 =
    accessibilityLabel && accessibilityLabel.type === "button"
      ? t("apk.install2Designed", { button: accessibilityLabel.label })
      : t("apk.install2");

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h2>{t("apk.title")}</h2>
          <p className="page-sub">{t("apk.intro")}</p>
        </div>
      </header>

      <section className="card">
        {/* The console's own form idiom: labelled fields that wrap as whole
            fields, and the one button that commits on a line of its own. */}
        <form
          className="col"
          onSubmit={(event) => {
            event.preventDefault();
            generate.mutate();
          }}
        >
          <div className="fields">
            <label className="field">
              <span className="field-label">{t("apk.label")}</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={128} />
              <span className="field-hint">{t("apk.labelHint")}</span>
            </label>
            <label className="field">
              {/* Managing keys lives in Settings; this only picks one. */}
              <span className="field-label">{t("apk.key")}</span>
              <select value={keyId} onChange={(e) => setKeyId(e.target.value)}>
                <option value="">{t("apk.newKey")}</option>
                {(keys.data ?? [])
                  .filter((key) => !key.revoked)
                  .map((key) => (
                    // A key from before secrets were kept has no plaintext
                    // left to bake. It stays on the list, greyed out and said
                    // so, rather than vanishing from the fleet's own keys.
                    <option key={key.id} value={key.id} disabled={!key.rebuildable}>
                      {key.rebuildable ? key.name : t("apk.keyLegacy", { name: key.name })}
                    </option>
                  ))}
              </select>
            </label>
            {!keyId && (
              <label className="field">
                <span className="field-label">{t("apk.cap")}</span>
                <input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} />
              </label>
            )}
            <label className="field">
              <span className="field-label">{t("apk.design")}</span>
              <select value={designId ?? ""} onChange={(e) => setDesignId(e.target.value)}>
                <option value="">{t("apk.builtInScreen")}</option>
                {(designs.data ?? []).map((design) => (
                  <option key={design.id} value={design.id}>
                    {design.is_default ? `${design.name} (${t("design.default")})` : design.name}
                  </option>
                ))}
              </select>
              <span className="field-hint">
                <Link to="/design">{t("apk.designLink")}</Link>
              </span>
            </label>
          </div>
          {/* What the phone will actually be able to do, before it is built. */}
          {uses && (
            <p className="muted small" style={{ margin: 0 }}>
              {t("apk.designUses", { design: chosen?.name ?? "", uses })}
            </p>
          )}
          <p className="muted small" style={{ margin: 0 }}>
            {t("apk.modulesInBuild", {
              modules: enabledModules.length
                ? enabledModules
                    .map((module) => localized(module.name, lang, module.id))
                    .join(", ")
                : t("apk.modulesNone"),
            })}{" "}
            <Link to="/modules">{t("apk.modulesLink")}</Link>
          </p>
          <div className="form-actions">
            {/* In the app the local build leads (D3): it is the one that costs
                the server nothing. In a browser there is only ever one button,
                and it stays exactly where it has always been (D5). */}
            {app && (
              <button
                className="primary"
                type="button"
                disabled={buildHere.isPending || generate.isPending}
                onClick={() => {
                  setSavedTo(null);
                  buildHere.mutate();
                }}
              >
                {buildHere.isPending ? t("apk.buildHereBusy") : t("apk.buildHere")}
              </button>
            )}
            <button
              className={app ? undefined : "primary"}
              type="submit"
              disabled={generate.isPending || buildHere.isPending}
            >
              {generate.isPending
                ? t("apk.generating")
                : app
                  ? t("apk.buildOnServer")
                  : t("apk.generate")}
            </button>
          </div>
          {app && (
            <>
              {/* The one thing a customer has to be told before they press it:
                  this APK is signed with a different key, so a phone running a
                  server build cannot upgrade to it in place (D4). */}
              <p className="muted small" style={{ margin: 0 }}>
                {t("apk.buildHereHint")}
              </p>
              {stage !== null && (
                <p className="muted small" style={{ margin: 0 }}>
                  {stageText(stage, t)}
                </p>
              )}
              {buildHere.isSuccess && savedTo !== null && (
                <p className="muted small" style={{ margin: 0 }}>
                  {t("apk.savedTo", { path: savedTo })}
                </p>
              )}
              {buildHere.isSuccess && savedTo === null && (
                <p className="muted small" style={{ margin: 0 }}>
                  {t("apk.buildCancelled")}
                </p>
              )}
            </>
          )}
        </form>
        {chosen && (
          <div className="design-apk-thumb">
            <PhonePreview
              spec={chosen.spec}
              scale={PICKER_SCALE}
              sampleRows={sampleRows(catalogue, lang)}
            />
          </div>
        )}
        {noTemplate ? (
          <p className="error-text">{t("apk.noTemplate")}</p>
        ) : designUnsupported ? (
          <p className="error-text">
            {t("apk.designUnsupported", {
              version: String(failure?.detail?.agent_version ?? "—"),
            })}
          </p>
        ) : moduleUnsupported ? (
          <p className="error-text">
            {t("apk.moduleUnsupported", {
              module: moduleName(catalogue, String(failure?.detail?.module ?? ""), lang),
              version: String(failure?.detail?.agent_version ?? "—"),
            })}
          </p>
        ) : moduleOff ? (
          <p className="error-text">
            {t("apk.designModuleDisabled", {
              design: String(failure?.detail?.design ?? chosen?.name ?? ""),
              modules: (Array.isArray(failure?.detail?.modules)
                ? (failure.detail.modules as string[])
                : []
              )
                .map((moduleId) => moduleName(catalogue, moduleId, lang))
                .join(", "),
            })}{" "}
            <Link to="/modules">{t("apk.modulesLink")}</Link>
          </p>
        ) : keyUnusable ? (
          <p className="error-text">{t("apk.keyUnavailable")}</p>
        ) : (
          problem && <ErrorBox error={problem} />
        )}
      </section>

      <section className="card">
        <h3>{t("apk.builds")}</h3>
        {builds.isLoading && <Loading what={t("apk.builds")} />}
        {builds.error && <ErrorBox error={builds.error} />}
        {builds.data?.length === 0 && <Empty>{t("apk.noBuilds")}</Empty>}
        {!!builds.data?.length && (
          <table>
            <thead>
              <tr>
                <th>{t("apk.created")}</th>
                <th>{t("apk.batchLabel")}</th>
                <th>{t("apk.version")}</th>
                <th>{t("apk.designColumn")}</th>
                <th>{t("apk.modulesColumn")}</th>
                <th>{t("apk.status")}</th>
                <th>{t("apk.size")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {builds.data.map((build) => (
                <tr key={build.id}>
                  <td>{relativeTime(build.created_at, t)}</td>
                  <td>{build.label ?? "—"}</td>
                  <td>{build.agent_version ?? "—"}</td>
                  <td>{build.design_name ?? t("apk.builtInScreen")}</td>
                  <td className="mono small">
                    {Object.keys(build.modules ?? {}).join(", ") || "—"}
                  </td>
                  <td>
                    <Badge value={build.status} />
                    {build.error && <div className="error-text small">{build.error}</div>}
                  </td>
                  <td>{formatSize(build.size)}</td>
                  <td>
                    {build.status === "ready" && (
                      <button
                        type="button"
                        disabled={download.isPending && download.variables === build.id}
                        onClick={() => download.mutate(build.id)}
                      >
                        {t("apk.download")}
                      </button>
                    )}
                    {/* Nothing to download: the file was written on someone's
                        computer and the server never held it (§4.1). The
                        checksum they reported is what ties the row to it. */}
                    {build.status === "local" && (
                      <span className="muted small">{t("apk.builtOnAComputer")}</span>
                    )}
                    {(build.status === "ready" || build.status === "local") && build.sha256 && (
                      <div
                        className="muted mono small"
                        title={`${t("apk.checksum")} ${build.sha256}`}
                      >
                        {build.sha256.slice(0, 12)}…
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {download.error && <ErrorBox error={download.error} />}
      </section>

      {app && (
        <section className="card">
          <h3>{t("apk.signingKey")}</h3>
          <p className="muted small">{t("apk.signingKeyIntro")}</p>
          {signingKey.data ? (
            <div className="fields">
              <div className="field">
                <span className="field-label">{t("apk.signingKeyFingerprint")}</span>
                <span className="mono small">{signingKey.data.fingerprint}</span>
              </div>
              <div className="field">
                <span className="field-label">{t("apk.signingKeyCreated")}</span>
                <span className="small">{relativeTime(signingKey.data.createdAt, t)}</span>
              </div>
            </div>
          ) : (
            <p className="muted small">{t("apk.signingKeyNone")}</p>
          )}
          <div className="fields">
            <label className="field">
              <span className="field-label">{t("apk.keyExportPassword")}</span>
              <input
                type="password"
                value={exportPassword}
                onChange={(e) => setExportPassword(e.target.value)}
              />
              <span className="field-hint">
                <button
                  type="button"
                  disabled={!exportPassword || exportKey.isPending}
                  onClick={() => exportKey.mutate()}
                >
                  {t("apk.exportKey")}
                </button>
              </span>
            </label>
            <label className="field">
              <span className="field-label">{t("apk.keyImportPassword")}</span>
              <input
                type="password"
                value={importPassword}
                onChange={(e) => setImportPassword(e.target.value)}
              />
              <span className="field-hint">
                <button
                  type="button"
                  disabled={!importPassword || importKey.isPending}
                  onClick={() => {
                    // Replacing the key means every APK built after this one
                    // has a different signature from every APK built before
                    // it (D4) — worth one confirmation.
                    if (window.confirm(t("apk.keyImportConfirm"))) importKey.mutate();
                  }}
                >
                  {t("apk.importKey")}
                </button>
              </span>
            </label>
          </div>
          {exportKey.data && "path" in exportKey.data && (
            <p className="muted small">{t("apk.keyExported", { path: exportKey.data.path })}</p>
          )}
          {importKey.data && "fingerprint" in importKey.data && (
            <p className="muted small">
              {t("apk.keyImported", { fingerprint: importKey.data.fingerprint })}
            </p>
          )}
          {exportKey.error && <ErrorBox error={exportKey.error} />}
          {importKey.error && <ErrorBox error={importKey.error} />}
        </section>
      )}

      <section className="card">
        <h3>{t("apk.install")}</h3>
        <ol>
          <li>{t("apk.install1")}</li>
          <li>{install2}</li>
          <li>{t("apk.install3")}</li>
          <li>{t("apk.install4")}</li>
        </ol>
      </section>
    </div>
  );
}
